/** Collection engine IDs remain unchanged; reports combine integrations for the same platform. */
export const REPORTING_PLATFORM_ENGINES = {
  chatgpt: ['chatgpt', 'chatgpt_searchapi'],
  gemini: ['gemini', 'gemini_searchapi'],
  perplexity: ['perplexity', 'perplexity_searchapi'],
} as const;

export function reportingPlatform(engine: string): string {
  const normalized = engine.trim().toLowerCase();
  for (const [platform, engines] of Object.entries(REPORTING_PLATFORM_ENGINES)) {
    if ((engines as readonly string[]).includes(normalized)) return platform;
  }
  return normalized;
}

/** Older saved visibility scores store counts per collection engine. Pool counts, not rates. */
export function combineReportingPlatformCounts(
  counts: Record<string, { n: number; k: number; p: number }>
): Record<string, { n: number; k: number; p: number }> {
  const combined: Record<string, { n: number; k: number; p: number }> = {};
  for (const [engine, count] of Object.entries(counts)) {
    const platform = reportingPlatform(engine);
    const total = combined[platform] ?? { n: 0, k: 0, p: 0 };
    total.n += count.n;
    total.k += count.k;
    total.p = total.n > 0 ? total.k / total.n : 0;
    combined[platform] = total;
  }
  return combined;
}

/** Trusted SQL column/expression only. Never pass request input as SQL text. */
export function reportingPlatformSql(column: string): string {
  const cases = Object.entries(REPORTING_PLATFORM_ENGINES)
    .map(([platform, engines]) => `WHEN '${engines[1]}' THEN '${platform}'`)
    .join(' ');
  return `(CASE ${column} ${cases} ELSE ${column} END)`;
}

/**
 * Match either integration even when an older URL/tool call supplies a provider ID.
 * Keep the stored column bare so PostgreSQL can use its engine indexes.
 * Both arguments are trusted SQL fragments; the platform value stays parameterized.
 */
export function reportingPlatformFilterSql(column: string, parameter: string): string {
  const cases = Object.values(REPORTING_PLATFORM_ENGINES)
    .flatMap(engines =>
      engines.map(
        engine => `WHEN '${engine}' THEN ARRAY[${engines.map(value => `'${value}'`).join(', ')}]`
      )
    )
    .join(' ');
  return `${column} = ANY(CASE LOWER(TRIM(${parameter}::text)) ${cases} ELSE ARRAY[LOWER(TRIM(${parameter}::text))] END)`;
}
